import { CheckIcon, X } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import {
  defaultPriorities,
  getPriorityIcon,
  getPriorityName,
  HighPriorityIcon,
  LowPriorityIcon,
  MediumPriorityIcon,
  NoPriorityIcon,
  Priority,
  UrgentPriorityIcon,
} from "@/types/priorities";

interface PriorityBadgeProps {
  priority?: number | null;
  className?: string;
  issueId?: string;
  variant?: "outline" | "default" | "text-dark";
  hasOutline?: boolean;
  onPriorityChange?: (priority: number | null) => void;
  borderless?: boolean;
}

export const PriorityBadge: React.FC<PriorityBadgeProps> = ({
  priority,
  className = "",
  issueId,
  variant = "default",
  hasOutline = false,
  onPriorityChange,
  borderless = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedId, setHighlightedId] = useState<
    number | "no-priority" | null
  >(null);
  const updateIssuePriority = useIssuesStore(
    (state) => state.updateIssuePriority,
  );
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const currentPriority = defaultPriorities.find((p) => p.id === priority);

  // Update highlighted item based on search query
  useEffect(() => {
    if (!searchQuery) {
      setHighlightedId(null);
      return;
    }

    const lowerQuery = searchQuery.toLowerCase();

    // Check "No priority" first
    if ("no priority".startsWith(lowerQuery)) {
      setHighlightedId("no-priority");
      return;
    }

    // Then check other priorities
    const matchingPriority = defaultPriorities.find((option) =>
      option.name.toLowerCase().startsWith(lowerQuery),
    );

    if (matchingPriority) {
      setHighlightedId(matchingPriority.id);
    } else {
      setHighlightedId(null);
    }
  }, [searchQuery]);

  const filteredPriorities = defaultPriorities.filter((option) =>
    option.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Focus input when dropdown opens and clear when it closes
  useEffect(() => {
    if (isOpen) {
      // Use a small timeout to ensure the dropdown is rendered
      const timeoutId = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchQuery("");
      setHighlightedId(null);
    }
  }, [isOpen]);

  const handlePriorityChange = async (
    e: React.MouseEvent,
    priorityValue: number | undefined,
  ) => {
    e.stopPropagation();

    if (onPriorityChange) {
      onPriorityChange(priorityValue ?? null);
      return;
    }

    if (!issueId) {
      toast.error("Could not update priority");
      return;
    }

    try {
      updateIssuePriority(supabaseClient, issueId ?? "", priorityValue ?? null);
      toast.success(
        priorityValue === undefined
          ? "Priority removed"
          : `Priority set to ${getPriorityName(priorityValue)}`,
      );
    } catch (error) {
      console.error("Failed to update priority:", error);
      toast.error("Failed to update priority");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && highlightedId !== null) {
      e.preventDefault();
      e.stopPropagation();
      if (highlightedId === "no-priority") {
        handlePriorityChange(e as any, undefined);
      } else {
        handlePriorityChange(e as any, highlightedId);
      }
      setIsOpen(false);
    }
  };

  const baseSpanClassName = `inline-flex items-center justify-center rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 text-xs text-secondary-n-400 cursor-pointer ${
    !borderless ? "border" : ""
  }`;

  const textDarkDropdownNode = (
    <button
      className={`${baseSpanClassName} ${className}`}
      data-state="closed"
      aria-label={`Priority: ${priority ?? "No priority"}`}
    >
      {priority ? (
        <span
          className={`${baseSpanClassName} gap-1 w-full h-7 px-2 bg-secondary-0 hover:bg-secondary-n-800 ${
            !borderless ? "border-border-primary" : ""
          }`}
        >
          {currentPriority ? (
            <currentPriority.icon className="w-4 h-4" />
          ) : (
            <NoPriorityIcon className="w-4 h-4" color="var(--text-primary)" />
          )}
          <span className="text-xs font-medium text-text-primary whitespace-nowrap">
            {currentPriority ? currentPriority.name : "No priority"}
          </span>
        </span>
      ) : (
        <span
          className={`${baseSpanClassName} gap-1 w-full h-7 px-2 bg-secondary-0 hover:bg-secondary-n-800 ${
            !borderless ? "border-secondary-n-900" : ""
          }`}
        >
          <NoPriorityIcon className="w-4 h-4" color="var(--secondary-n-600)" />
          <span className="text-xs font-medium text-secondary-n-400">
            Priority
          </span>
        </span>
      )}
    </button>
  );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <div>
          {variant === "text-dark" ? (
            textDarkDropdownNode
          ) : (
            <Badge
              variant={hasOutline ? "outline" : "default"}
              className={cn(
                "h-6 w-6 p-0 flex items-center justify-center",
                hasOutline && !borderless && "border border-border-primary",
                "text-muted-foreground hover:bg-secondary-n-800 cursor-pointer",
                !hasOutline && "border-0",
                className,
              )}
            >
              {currentPriority ? (
                <currentPriority.icon className="w-4 h-4" />
              ) : (
                <NoPriorityIcon className="w-4 h-4" />
              )}
            </Badge>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        ref={menuRef}
        className="w-[200px] overflow-hidden bg-secondary-0 rounded-lg"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          searchInputRef.current?.focus();
        }}
        onPointerDownOutside={(e) => {
          // Only close if clicking outside the dropdown
          if (!menuRef.current?.contains(e.target as Node)) {
            setIsOpen(false);
          }
        }}
        onInteractOutside={(e) => {
          // Prevent closing when interacting with the input
          if (searchInputRef.current?.contains(e.target as Node)) {
            e.preventDefault();
          }
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-1">
          <div className="relative">
            <Input
              ref={searchInputRef}
              placeholder="Change priority"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-7 text-sm bg-secondary-n-950 border-0! border-none! focus:border-0! focus:border-none! focus-visible:border-0! focus-visible:border-none! focus-visible:ring-0! focus-visible:!ring-none placeholder:text-secondary-n-600 pr-6"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={handleKeyDown}
              onBlur={(e) => {
                if (menuRef.current?.contains(e.relatedTarget as Node)) {
                  e.preventDefault();
                  searchInputRef.current?.focus();
                }
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={(e) => handlePriorityChange(e, undefined)}
          className={cn(
            highlightedId === "no-priority" && "bg-secondary-n-800",
          )}
        >
          <div className="flex items-center justify-between w-full py-1 pl-2">
            <div className="flex items-center gap-2">
              <NoPriorityIcon className="w-3.5 h-3.5 shrink-0" />
              <span>No priority</span>
            </div>
            {!priority && <CheckIcon className="size-3.5" color="#F5F5F6" />}
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="ml-2 caption text-2xs font-medium text-secondary-n-400">
          Priorities
        </DropdownMenuLabel>
        {filteredPriorities.map((option) => (
          <DropdownMenuItem
            key={option.id}
            onClick={(e) => handlePriorityChange(e, option.id)}
            className={cn(highlightedId === option.id && "bg-secondary-n-800")}
          >
            <div className="flex items-center justify-between w-full py-1 pl-2">
              <div className="flex items-center gap-2">
                <option.icon className="w-4 h-4" />
                <span>{option.name}</span>
              </div>
              {priority === option.id && (
                <CheckIcon className="size-3.5" color="#F5F5F6" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default PriorityBadge;
